import { useEffect } from "react";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  buildCanonicalUrl,
  type PageSeo,
} from "@/content/seo";
import { site } from "@/content/site";
import {
  removeJsonLd,
  removeLinkTag,
  removeMetaTag,
  setJsonLd,
  setLinkTag,
  setMetaTag,
} from "@/lib/head";

type PageMetaProps = PageSeo & {
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
};

export default function PageMeta({
  title,
  description,
  path,
  ogType = "website",
  noindex = false,
  includeCanonical = true,
  jsonLd = null,
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    setMetaTag("name", "description", description);

    if (noindex) {
      setMetaTag("name", "robots", "noindex, nofollow");
      removeLinkTag("canonical");
    } else {
      removeMetaTag("name", "robots");

      if (includeCanonical) {
        setLinkTag("canonical", buildCanonicalUrl(path));
      } else {
        removeLinkTag("canonical");
      }
    }

    const pageUrl = buildCanonicalUrl(path);
    const imageAlt = `${site.practitioner.fullTitle} — psychotherapy practice`;

    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", pageUrl);
    setMetaTag("property", "og:image", DEFAULT_OG_IMAGE);
    setMetaTag("property", "og:image:secure_url", DEFAULT_OG_IMAGE);
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:image:alt", imageAlt);
    setMetaTag("property", "og:locale", "en_US");

    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", DEFAULT_OG_IMAGE);
    setMetaTag("name", "twitter:image:alt", imageAlt);

    if (jsonLd) {
      setJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }
  }, [title, description, path, ogType, noindex, includeCanonical, jsonLd]);

  return null;
}
